import {
  GetJoinRequestsAllUserDataResponse,
  GetJoinRequestsAllUserDataVariables,
  JoinRequestData,
  getJoinRequestsAllUserData,
} from "../graphqlQueries/getJoinRequests";
import { JoinRequestStatus, UserRole } from "../types";
import React, { useEffect, useState } from "react";
import getUserProfilesByCompanyQuery, {
  BasicUserProfile,
  GetUserProfilesByCompanyResult,
  GetUserProfilesByCompanyVariables,
} from "../graphqlQueries/getUserProfilesByCompany";

import Notification from "../components/Notification";
import Page from "./Page";
import PageHeader from "../components/PageHeader";
import UserManagementTable from "../components/userManagement/UserManagementTable";
import { getHasuraRoleContext } from "../utils/functions";
import { useQuery } from "@apollo/client";
import useRequestNotifications from "../components/hooks/useNotifications";
import { useTranslation } from "react-i18next";

export type ApprovedUserData = BasicUserProfile & {
  approvedAt: string;
  approvedBy: string;
};

type UserManagementProps = {
  companyId: string;
};

const UserManagement = ({ companyId }: UserManagementProps) => {
  const { t } = useTranslation();

  const [approvedUserData, setApprovedUserData] = useState<ApprovedUserData[]>([]);

  const { notifications, removeNotification } = useRequestNotifications();

  const { data: joinRequestData } = useQuery<
    GetJoinRequestsAllUserDataResponse,
    GetJoinRequestsAllUserDataVariables
  >(getJoinRequestsAllUserData, {
    context: getHasuraRoleContext(UserRole.ManageCompany),
    variables: {
      companyId: companyId ?? "",
    },
  });

  const { data: userData } = useQuery<
    GetUserProfilesByCompanyResult,
    GetUserProfilesByCompanyVariables
  >(getUserProfilesByCompanyQuery, {
    context: getHasuraRoleContext(UserRole.ManageCompany),
    fetchPolicy: "cache-and-network",
    variables: { companyId },
  });

  useEffect(() => {
    if (joinRequestData && userData && userData.userFullProfileByCompany.length > 0) {
      const approvedJoinRequests = joinRequestData.companyJoinRequest.reduce<
        Record<string, JoinRequestData>
      >((map, req) => {
        if (req.status === JoinRequestStatus.Approved) {
          map[req.user.id] = req;
        }
        return map;
      }, {});
      const approvedUserData = userData.userFullProfileByCompany.map<ApprovedUserData>((user) => {
        const userJoinReq = approvedJoinRequests[user.id];
        return {
          ...user,
          approvedAt: userJoinReq?.updatedAt ?? "",
          approvedBy: userJoinReq?.updatedBy ?? "",
        };
      });
      setApprovedUserData(approvedUserData);
    }
  }, [joinRequestData, userData]);

  return (
    <Page>
      <div className="sticky-top bg-white">
        {notifications.map((data) => (
          <Notification
            className="mb-1"
            key={data.key}
            type={data.resultStatus === "ok" ? "success" : "danger"}
            onClose={() => removeNotification(data.key)}
          >
            {data.message}
          </Notification>
        ))}
      </div>
      <div className="p-3 m-md-3 mx-lg-4">
        <PageHeader>{t("userManagement.pageTitle")}</PageHeader>
        <p>{t("userManagement.pageDescription")}</p>
        <p>{t("userManagement.pageDescriptionDesc")}</p>
        <div className="mt-5">
          <h2 className="text-landing-h4 mb-4">{t("userManagement.usersTableTitle")}</h2>
          <UserManagementTable users={approvedUserData} />
        </div>
      </div>
    </Page>
  );
};

export default UserManagement;
